"use strict";
/*
 * Integration App React package
 * {@link https://www.npmjs.com/package/@integration-app/react}
 * v2.22.1
 */
'use client';

import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import clsx from 'clsx';
import { SvgIcon } from '../../../SvgIcon/index.mjs';
import { SvgIconType } from '../../../SvgIcon/svg-icon-type.mjs';
import useEventStopPropagation from '../../../../helpers/hooks/useEventStopPropagation.mjs';
import '../../../../styled-system/css/css.mjs';
import '../../../../styled-system/helpers.mjs';
import { sva } from '../../../../styled-system/css/sva.mjs';

const editorTagBadgeRecipe = sva({
  className: "editor-tag-badge",
  slots: ["root", "icon", "close-button-container", "close-button"],
  base: {
    root: {
      position: "relative",
      display: "flex",
      flexDir: "row",
      flexWrap: "nowrap",
      alignItems: "center",
      justifyContent: "start",
      maxW: "full",
      minH: "5",
      gap: "0.5",
      px: "1.5",
      py: "0.5",
      borderWidth: "1px",
      borderStyle: "solid",
      rounded: "lg",
      fontSize: "token(sizes.3)",
      lineHeight: "token(sizes.4)",
      fontWeight: "medium",
      bg: "colorPalette.3",
      borderColor: "colorPalette.4",
      color: "colorPalette.12",
      userSelect: "none",
      _hover: {
        cursor: "pointer",
        bg: "colorPalette.4",
        borderColor: "colorPalette.5"
      },
      "&:hover .editor-tag-badge__close-button-container": {
        display: "flex",
        gradientVia: "colorPalette.4!",
        gradientTo: "colorPalette.4!"
      },
      _active: {
        bg: "colorPalette.5",
        borderColor: "colorPalette.6"
      },
      "[data-selected='true'] &, [data-tag-dropdown-open='true'] &": {
        bg: "colorPalette.5",
        borderColor: "colorPalette.6",
        "& .editor-tag-badge__close-button-container": {
          gradientFrom: "transparent",
          gradientVia: "colorPalette.5",
          gradientTo: "colorPalette.5"
        }
      },
      "[aria-disabled='true'] &": {
        pointerEvents: "none",
        bg: "slate.4",
        borderColor: "slate.5",
        color: "black",
        cursor: "initial"
      }
    },
    icon: {
      h: "3",
      w: "3",
      minW: "3",
      strokeWidth: "token(sizes.0.5)"
    },
    "close-button-container": {
      display: "none",
      justifyContent: "flex-end",
      alignItems: "center",
      position: "absolute",
      right: "1",
      strokeWidth: "token(sizes.0.5)",
      borderTopRightRadius: "md",
      borderBottomRightRadius: "md",
      w: "7",
      maxW: "1/2",
      h: "full",
      bgGradient: "to-r",
      gradientFrom: "transparent",
      "[data-selected='true'] &, [data-tag-dropdown-open='true'] &": {
        display: "flex"
      }
    },
    "close-button": {
      rounded: "sm",
      outline: "none",
      _focus: {
        bg: "colorPalette.6",
        "& svg": {
          strokeWidth: "3px"
        }
      },
      "& svg": {
        h: "3",
        w: "3",
        _hover: {
          strokeWidth: "3px"
        }
      }
    }
  },
  variants: {
    variant: {
      "static-value": {
        root: {
          color: "black",
          colorPalette: "gray"
        }
      },
      formula: {
        root: {
          colorPalette: "indigo"
        }
      },
      value: {
        root: {
          colorPalette: "blue"
        }
      },
      error: {
        root: {
          colorPalette: "tomato",
          color: "tomato.10"
        }
      },
      ghost: {
        root: {
          bg: "transparent!",
          borderColor: "transparent!"
        },
        "close-button": {
          display: "none!"
        }
      }
    },
    disabled: {
      true: {
        root: {
          pointerEvents: "none",
          bg: "slate.4",
          borderColor: "slate.5",
          color: "black",
          cursor: "initial"
        }
      },
      false: {}
    }
  },
  defaultVariants: {
    variant: "value"
  }
});
const EditorTagBadge = forwardRef(({ children, className, variant, disabled, onDelete, ...rest }, ref) => {
  const styles = editorTagBadgeRecipe({ variant, disabled });
  const handleOnClick = useEventStopPropagation(() => onDelete?.());
  return /* @__PURE__ */ jsxs("div", { className: clsx(styles.root, className), ...rest, ref, children: [
    children,
    /* @__PURE__ */ jsx("div", { className: styles["close-button-container"], children: /* @__PURE__ */ jsx("button", { className: styles["close-button"], disabled, "aria-label": "Delete", onClick: handleOnClick, children: /* @__PURE__ */ jsx(SvgIcon, { type: SvgIconType.Close, className: styles.icon }) }) })
  ] });
});
EditorTagBadge.displayName = "EditorTagBadge";
function EditorTagBadgeIcon({ type, className }) {
  const styles = editorTagBadgeRecipe();
  return /* @__PURE__ */ jsx(SvgIcon, { type, className: clsx(styles.icon, className) });
}

export { EditorTagBadge, EditorTagBadgeIcon, editorTagBadgeRecipe };
